import { AuthClient } from "@dfinity/auth-client";
import { AnonymousIdentity, type Identity } from "@dfinity/agent";

const II_URL = "https://id.ai";
const LOCAL_II_URL = "http://id.ai.localhost:4943";
const DERIVATION_ORIGIN = "https://c7bzy-faaaa-aaaao-ba4la-cai.icp0.io";
const SESSION_NS = BigInt(8 * 24) * BigInt(3_600_000_000_000);

export function isLocalHostname(hostname: string): boolean {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.endsWith(".localhost")
  );
}

export function isDevEnvironment(): boolean {
  return typeof window !== "undefined" && isLocalHostname(window.location.hostname);
}

let clientPromise: Promise<AuthClient> | null = null;

async function getClient(): Promise<AuthClient> {
  if (!clientPromise) {
    clientPromise = AuthClient.create({ idleOptions: { disableIdle: true } });
  }
  return clientPromise;
}

export async function restoreIdentity(): Promise<Identity | null> {
  const client = await getClient();
  try {
    return (await client.isAuthenticated()) ? client.getIdentity() : null;
  } catch {
    return null;
  }
}

export async function signIn(): Promise<Identity> {
  const client = await getClient();
  await new Promise<void>((resolve, reject) => {
    client.login({
      identityProvider: isDevEnvironment() ? LOCAL_II_URL : II_URL,
      derivationOrigin: isDevEnvironment() ? undefined : DERIVATION_ORIGIN,
      maxTimeToLive: SESSION_NS,
      onSuccess: resolve,
      onError: reject,
    });
  });
  return client.getIdentity();
}

export function signInAnonymously(): Identity {
  if (!isDevEnvironment()) {
    throw new Error("Anonymous sign-in is only available on local origins.");
  }
  return new AnonymousIdentity();
}

export async function signOut(): Promise<void> {
  const client = await getClient();
  await client.logout();
  clientPromise = null;
}
